import React, { MouseEvent, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  SnackbarProps,
  Stack,
  Tooltip,
  Typography,
  IconButton,
  Button,
  Box,
} from "@mui/material";
import _debounce from "lodash/debounce";
// third-party
import { ColumnDef } from "@tanstack/react-table";

import {
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  CloseOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import Loader from "../../../../../../components/Loader";
import { FormattedMessage } from "react-intl";
import { USERAXIOS } from "../../../../../../api/userAxios";
import ReusableTable from "../../../../../../components/ReusableComponent/ReusableTable";
import { openSnackbar } from "../../../../../../api/snackbar";
import ReusableDeleteDialog from "../../../../../../components/ReusableComponent/ReusableDeleteDialog";
import { Order } from "../../../../../../types/orders-managment/orders";
import ExpandingOrderDetail from "./ExpandingOrderDetails";
import FiltersModal, { VacancyFiltersType } from "./FiltersModal";

// ==============================|| OrderS LIST ||============================== //

const Orders = () => {
  const theme = useTheme();
  const location = useLocation();
  const [open, setOpen] = useState<boolean>(false);
  const [openFilters, setOpenFilters] = useState<boolean>(false);

  const [selected, setSelected] = useState<any>("");
  const [expandedRowId, setExpandedRowId] = useState<string | number | null>(
    null
  );

  const handleClose = () => {
    setOpen(!open);
  };

  const handleFiltersModalClose = () => {
    setOpenFilters(false);
  };

  const handleRowExpand = (rowId: string | number) => {
    setExpandedRowId(expandedRowId === rowId ? null : rowId);
  };

  const columns = useMemo<ColumnDef<Order>[]>(
    () => [
      {
        header: () => <FormattedMessage id="ID" />,
        accessorKey: "id",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.id}</Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="User ID" />,
        accessorKey: "company_id",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.company_name || "-"}
          </Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Fees" />,
        accessorKey: "fees",
        meta: { className: "cell-center" },
        cell: ({ row }) => {
          const fees = row.original.order_items?.[0]?.packable?.fees ?? "-";
          return <Typography variant="subtitle2">{fees}</Typography>;
        },
      },
      {
        header: () => <FormattedMessage id="Count" />,
        accessorKey: "count",
        meta: { className: "cell-center" },
        cell: ({ row }) => {
          const count = row.original.order_items?.[0]?.packable?.count ?? "-";
          return <Typography variant="subtitle2">{count}</Typography>;
        },
      },
      {
        header: () => <FormattedMessage id="Price per unit" />,
        accessorKey: "price_per_unit",
        meta: { className: "cell-center" },
        cell: ({ row }) => {
          const price =
            row.original.order_items?.[0]?.packable?.price_per_unit ?? "-";
          return <Typography variant="subtitle2">{price}</Typography>;
        },
      },
      {
        header: () => <FormattedMessage id="Validity" />,
        accessorKey: "package_validity",
        meta: { className: "cell-center" },
        cell: ({ row }) => {
          const validity =
            row.original.order_items?.[0]?.packable?.package_validity ?? "-";
          return <Typography variant="subtitle2">{validity}</Typography>;
        },
      },
      {
        header: () => <FormattedMessage id="Duration" />,
        accessorKey: "package_duration",
        meta: { className: "cell-center" },
        cell: ({ row }) => {
          const duration =
            row.original.order_items?.[0]?.packable?.package_duration ?? "-";
          return <Typography variant="subtitle2">{duration}</Typography>;
        },
      },
      {
        header: () => <FormattedMessage id="Created At" />,
        accessorKey: "created_at",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.created_at
              ? row.original.created_at.split("T")[0]
              : "-"}
          </Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Status" />,
        accessorKey: "status",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.status || "-"}
          </Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Actions" />,
        accessorKey: "actions",
        meta: { className: "cell-center" },
        disableSortBy: true,
        cell: ({ row }) => {
          const isExpanded = expandedRowId === row.original.id;
          const collapseIcon = isExpanded ? (
            <CloseOutlined style={{ color: theme.palette.error.main }} />
          ) : (
            <EyeOutlined />
          );

          const handleEditOrder = () => {
            window.location.href = `/orders/edit-order/${row.original.id}`;
          };
          return (
            <Stack direction="row" spacing={0}>
              <Tooltip title={isExpanded ? "Collapse" : "View Details"}>
                <IconButton
                  color="secondary"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleRowExpand(row.original.id);
                  }}
                >
                  {collapseIcon}
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton
                  color="primary"
                  onClick={handleEditOrder}
                  sx={{
                    "&:hover": { backgroundColor: "transparent" },
                  }}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleClose();
                    setSelected(row.original);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [theme, expandedRowId]
  );

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Order[]>([]);
  const [countPage, setCountPage] = useState({
    current_page: 0,
    last_page: 0,
  });
  const [currentPage, setCurrenttPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchedtext, setSearchedtext] = useState("");
  const [loadingSearsh, setloadingSearsh] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!selected?.id) return;
    setIsDeleting(true);
    try {
      await USERAXIOS.delete(`/order/${selected?.id}`);
      setResult((prevResults: any[]) =>
        prevResults.filter((item) => item.id !== selected?.id)
      );
      openSnackbar({
        open: true,
        message: "Order deleted successfully",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        close: false,
        alert: {
          color: "success",
        },
      } as SnackbarProps);
      if (handleClose) handleClose();
    } catch (error: any) {
      openSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to delete order",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const id = urlParams.get("id");
      const company_id = urlParams.get("company_id");
      const from = urlParams.get("from");
      const to = urlParams.get("to");
      const duration = urlParams.get("duration");
      const status = urlParams.get("status");

      const response = await USERAXIOS.get(
        `/order?page=${currentPage}${searchQuery ? `&id=${searchQuery}` : ""}` +
          (id ? `&id=${id}` : "") +
          (company_id ? `&company_id=${company_id}` : "") +
          (from ? `&from=${from}` : "") +
          (to ? `&to=${to}` : "") +
          (duration ? `&duration=${duration}` : "") +
          (status ? `&status=${status}` : "")
      );
      const { data, meta } = response.data;
      setResult(data);
      setCountPage(meta);
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching orders",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [location.search]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrenttPage(page);
  };

  const handleSearch = () => {
    setloadingSearsh(true);
    setCurrenttPage(1);
    setSearchQuery(searchedtext);
    setloadingSearsh(false);
  };

  const handleApplyFilters = (filters: VacancyFiltersType) => {
    setLoading(true);
    const { id, company_id, from, to, duration, status } = filters;
    const urlParams = new URLSearchParams(window.location.search);

    // Clear existing filter params
    urlParams.delete("id");
    urlParams.delete("company_id");
    urlParams.delete("from");
    urlParams.delete("to");
    urlParams.delete("duration");
    urlParams.delete("status");

    // Set new filter params
    if (id) urlParams.set("id", id);
    if (company_id) urlParams.set("company_id", company_id);
    if (from) urlParams.set("from", from);
    if (to) urlParams.set("to", to);
    if (duration) urlParams.set("duration", duration);
    if (status) urlParams.set("status", status);

    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.pushState({}, "", newUrl);

    setCurrenttPage(1);
    fetchData();
  };

  const handleClearFilters = () => {
    setLoading(true);
    window.history.pushState({}, "", `${window.location.pathname}`);
    setCurrenttPage(1);
    fetchData();
  };

  const hasActiveFilters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return !!(
      urlParams.get("id") ||
      urlParams.get("company_id") ||
      urlParams.get("from") ||
      urlParams.get("to") ||
      urlParams.get("duration") ||
      urlParams.get("status")
    );
  };

  const enhancedData = useMemo(() => {
    return result.map((item) => ({
      ...item,
      getCanExpand: () => true,
      getIsExpanded: () => expandedRowId === item.id,
      getToggleExpandedHandler: () => () => handleRowExpand(item.id),
    }));
  }, [result, expandedRowId]);

  // const hasActiveFilters = activeFilters && Object.values(activeFilters).some(value => value !== "");

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 2 }}>
              {hasActiveFilters() && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleClearFilters}
                  sx={{ padding: ".3rem 2rem" }}
                >
                  <FormattedMessage id="Clear Filters" />
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<FilterOutlined />}
                onClick={() => setOpenFilters(true)}
                sx={{
                  padding: ".3rem 2rem",
                  gap: ".5rem",
                }}
              >
                <FormattedMessage id="Filters" />
              </Button>
            </Box>
          </Box>

          <ReusableTable
            data={enhancedData}
            columns={columns}
            countPage={countPage}
            handlePageChange={handlePageChange}
            handleSearchChange={handleSearch}
            currentPage={currentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            setCurrenttPage={setCurrenttPage}
            loadingSearsh={loadingSearsh}
            searchedtext={searchedtext}
            setSearchedtext={setSearchedtext}
            renderDetailPanel={({ row }: any) => (
              <ExpandingOrderDetail data={row.original} />
            )}
            expandedRowId={expandedRowId}
            setExpandedRowId={setExpandedRowId}
          />
        </>
      )}

      {openFilters && (
        <FiltersModal
          open={openFilters}
          handleClose={handleFiltersModalClose}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
        />
      )}

      <ReusableDeleteDialog
        title={selected?.name}
        open={open}
        handleClose={handleClose}
        handleDelete={handleDelete}
        isDeleting={isDeleting}
      />
    </>
  );
};
export default Orders;
